import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";

// Vision UI Dashboard PRO React components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

// Vision UI Dashboard PRO React example components
import DataTable from "examples/Tables/DataTable";

// API Services
import { takedownsApi } from "services/takedownsApi";

// Helper function to extract root domain
const extractRootDomain = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch (e) {
    return url;
  }
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
};

// Helper to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'removed':
      return 'success';
    case 'submitted':
      return 'info';
    case 'pending':
      return 'warning';
    case 'rejected':
      return 'error';
    default:
      return 'text';
  }
};

function ThreatDetails() {
  const { threatID } = useParams();
  
  // State management
  const [threatData, setThreatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState({ columns: [], rows: [] });
  
  // Pagination and filtering state
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalEntries, setTotalEntries] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);

  // Fetch threat details (stats only) on mount
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await takedownsApi.fetchThreatDetails(threatID);
        setThreatData(data);
      } catch (err) {
        setError('Failed to load threat actor details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (threatID) {
      fetchDetails();
    }
  }, [threatID]);

  // Fetch paginated takedowns data
  useEffect(() => {
    const fetchTakedownsData = async () => {
      if (!threatData) return;
      
      try {
        setTableLoading(true);
        
        // Fetch paginated content filtered by this threat actor's username
        const params = {
          page: currentPage,
          limit: entriesPerPage,
          username: threatData.threatStats?.username,
          search: searchTerm || undefined,
        };
        
        const response = await takedownsApi.fetchOptimizedContent(params);
        
        // Transform data for table
        const transformedData = transformTakedownsData(response.data || []);
        setTableData(transformedData);
        setTotalEntries(response.total || 0);
      } catch (err) {
        setError('Failed to load takedowns data.');
      } finally {
        setTableLoading(false);
      }
    };
    
    fetchTakedownsData();
  }, [threatData, currentPage, entriesPerPage, searchTerm]);

  // Transform takedowns data for DataTable
  const transformTakedownsData = (takedowns) => {
    const columns = [
      { Header: 'TAKEDOWN ID', accessor: 'takedownId', width: '10%' },
    { Header: 'PLATFORM', accessor: 'platform', width: '15%' },
    { Header: 'INFRINGEMENT TYPE', accessor: 'infringementType', width: '12%' },
    { Header: 'MEDIA TYPE', accessor: 'mediaType', width: '10%' },
    { Header: 'STATUS', accessor: 'status', width: '10%' },
      { Header: 'VIEWS', accessor: 'views', width: '8%' },
    { Header: 'DATE LAST ACTIVITY', accessor: 'dateLastActivity', width: '15%' },
    ];

    const rows = takedowns.map((item) => ({
      takedownId: (
        <Link to={`/Dashboards/Takedowns/${item.id}/details`} style={{ textDecoration: 'none' }}>
          <VuiTypography variant="button" color="white" fontWeight="medium" sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            {item.id}
          </VuiTypography>
        </Link>
      ),
      platform: (
        <Link to={`/Dashboards/Platforms/${item.root_domain}/details`} style={{ textDecoration: 'none' }}>
          <VuiTypography variant="button" color="info" fontWeight="medium" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
            {item.root_domain}
          </VuiTypography>
        </Link>
      ),
      infringementType: (
        <VuiTypography variant="button" color="text" fontWeight="regular">
          {item.violation_type}
        </VuiTypography>
      ),
      mediaType: (
        <VuiTypography variant="button" color="text" fontWeight="regular">
          {item.media_type}
        </VuiTypography>
      ),
      status: (
        <VuiBox display="flex" alignItems="center" gap={1}>
          <VuiBox 
            width="8px" 
            height="8px" 
            borderRadius="50%" 
            bgColor={getStatusColor(item.takedown_status)}
          />
          <VuiTypography variant="button" color="text" fontWeight="regular">
            {item.takedown_status}
          </VuiTypography>
        </VuiBox>
      ),
      views: (
        <VuiTypography variant="button" color="text" fontWeight="regular">
          {item.views || 'N/A'}
        </VuiTypography>
      ),
      dateLastActivity: (
        <VuiTypography variant="button" color="text" fontWeight="regular">
          {formatDate(item.last_activity)}
        </VuiTypography>
      ),
    }));

    return { columns, rows };
  };

  // Pagination and search handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleEntriesPerPageChange = (newEntriesPerPage) => {
    setEntriesPerPage(newEntriesPerPage);
    setCurrentPage(1); // Reset to first page when changing entries per page
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Calculate statistics
  const calculateStats = () => {
    if (!threatData) return null;
    
    const stats = threatData.threatStats || {};
    const complied = stats.num_removed || 0;
    const submitted = stats.num_submitted || 0;
    const total = stats.num_detected || 0;
    const complianceRate = submitted > 0 ? Math.round((complied / submitted) * 100) : 0;
    
    return {
      username: stats.username || 'Unknown',
      totalTakedowns: total,
      submitted,
      complied,
      uncomplied: total - complied,
      pending: stats.num_pending || 0,
      complianceRate,
      platformCount: threatData.threatPlatforms?.length || 0,
      platforms: (threatData.threatPlatforms || []).map(p => p.root_domain).filter(Boolean).join(', ') || 'N/A'
    };
  };

  const stats = calculateStats();

  // Calculate priority and recommendation
  const getPriority = () => {
    if (!stats) return { level: 'Low', reason: '', recommendation: '' };
    
    if (stats.totalTakedowns > 100 && stats.complianceRate < 50) {
      return {
        level: 'High',
        reason: 'High volume of content distribution across multiple platforms with poor compliance rate.',
        recommendation: 'This threat actor shows a pattern of non-compliance across multiple platforms. We recommend escalating to legal action and coordinating with platform providers for enhanced enforcement measures.'
      };
    } else if (stats.totalTakedowns > 50 || stats.complianceRate < 70) {
      return {
        level: 'Medium',
        reason: 'Moderate activity with some compliance issues.',
        recommendation: 'Monitor this threat actor closely and consider enhanced enforcement if activity increases or compliance worsens.'
      };
    } else {
      return {
        level: 'Low',
        reason: 'Low volume or good compliance rate.',
        recommendation: 'Continue standard monitoring and enforcement procedures.'
      };
    }
  };

  const priority = getPriority();

  const handleReject = () => {
  };

  const handleHold = () => {
  };

  const handleAlertMB = () => {
  };

  // Show loading state
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress color="info" size={60} />
        </VuiBox>
      </DashboardLayout>
    );
  }

  // Show error state
  if (error || !threatData || !stats) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3}>
          <Alert severity="error">{error || 'Threat actor not found'}</Alert>
        </VuiBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3} sx={{ overflow: "visible" }}>
        {/* Header */}
        <VuiBox mb={3}>
          <VuiTypography variant="h3" color="white" fontWeight="bold">
            Threat Actor Details - {stats.username}
          </VuiTypography>
        </VuiBox>

        <Grid container spacing={3} alignItems="stretch">
          {/* Threat Actor Overview Card */}
          <Grid item xs={12} lg={7}>
            <Card sx={{ 
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(60px)",
              borderRadius: "20px",
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}>
              <VuiBox>
                <VuiTypography variant="h6" color="white" fontWeight="bold" mb={3}>
                  Threat Actor Overview
                </VuiTypography>

                <Grid container spacing={3}>
                  {/* General Section */}
                  <Grid item xs={12} md={4} lineHeight="1.2">
                    <VuiBox>
                      <VuiTypography 
                        variant="button" 
                        color="white" 
                        fontWeight="bold" 
                        mb={2}
                        sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                      >
                        GENERAL
                      </VuiTypography>
                      
                      <VuiBox display="flex" flexDirection="column" gap={2} lineHeight="1.2">
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            USERNAME
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {stats.username}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            ALIAS
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {stats.username}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            JURISDICTION
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            Unknown
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            INFRINGEMENT TYPE(S)
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            Explicit
                          </VuiTypography>
                        </VuiBox>
                      </VuiBox>
                    </VuiBox>
                  </Grid>

                  {/* Activity Section */}
                  <Grid item xs={12} md={4} lineHeight="1.2">
                    <VuiBox>
                      <VuiTypography 
                        variant="button" 
                        color="white" 
                        fontWeight="bold" 
                        mb={2}
                        sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                      >
                        ACTIVITY
                      </VuiTypography>
                      
                      <VuiBox display="flex" flexDirection="column" gap={2} lineHeight="1.2">
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            PLATFORMS
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px', wordBreak: 'break-word' }}>
                            {stats.platforms}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            PLATFORM COUNT
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {stats.platformCount}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            CONTENT COUNT
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {stats.totalTakedowns}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            POST COUNT
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {stats.totalTakedowns}
                          </VuiTypography>
                        </VuiBox>
                      </VuiBox>
                    </VuiBox>
                  </Grid>

                  {/* Takedown Stats Section */}
                  <Grid item xs={12} md={4} lineHeight="1.2">
                    <VuiBox>
                      <VuiTypography 
                        variant="button" 
                        color="white" 
                        fontWeight="bold" 
                        mb={2}
                        sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                      >
                        TAKEDOWN STATS
                      </VuiTypography>
                      
                      <VuiBox display="flex" flexDirection="column" gap={2} lineHeight="1.2">
                      <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            TOTAL TAKEDOWNS
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {stats.totalTakedowns}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            SUBMITTED
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {stats.submitted}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            COMPLIED
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {stats.complied}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            PENDING
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {stats.pending}
                          </VuiTypography>
                        </VuiBox>
                        
                        <VuiBox>
                          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                            COMPLIANCE RATE
                          </VuiTypography>
                          <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                            {stats.complianceRate}%
                          </VuiTypography>
                        </VuiBox>
                      </VuiBox>
                    </VuiBox>
                  </Grid>
                </Grid>
              </VuiBox>
            </Card>
          </Grid>

          {/* Threat Assessment & Action Card */}
          <Grid item xs={12} lg={5}>
            <Card sx={{ 
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(60px)",
              borderRadius: "20px",
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}>
              <VuiBox display="flex" flexDirection="column" height="100%">
                <VuiTypography variant="h6" color="white" fontWeight="bold" mb={3}>
                  Threat Assessment & Action
                </VuiTypography>

                <VuiBox display="flex" flexDirection="column" gap={3} flex={1}>
                  {/* Priority */}
                    <VuiBox>
                    <VuiBox display="flex" alignItems="center" gap={1} mb={1}>
                      <VuiBox 
                        width="12px" 
                        height="12px" 
                        borderRadius="50%" 
                        bgColor={priority.level === 'High' ? 'error' : priority.level === 'Medium' ? 'warning' : 'success'}
                      />
                      <VuiTypography variant="button" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                        PRIORITY
                      </VuiTypography>
                    </VuiBox>
                    <VuiTypography variant="h4" color="white" fontWeight="bold">
                      {priority.level}
                      </VuiTypography>
                    </VuiBox>
                    
                  {/* Priority Reason */}
                    <VuiBox>
                    <VuiTypography variant="button" color="text" fontWeight="medium" mb={1} sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                        PRIORITY REASON
                      </VuiTypography>
                    <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', fontSize: '14px' }}>
                      {priority.reason}
                      </VuiTypography>
                  </VuiBox>

                  <Divider light sx={{ my: 1 }} />

                  {/* Recommendation */}
                  <VuiBox>
                    <VuiTypography variant="button" color="text" fontWeight="medium" mb={1} sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                    RECOMMENDATION
                  </VuiTypography>
                    <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', fontSize: '14px' }}>
                      {priority.recommendation}
                  </VuiTypography>
                </VuiBox>

                {/* Action Buttons */}
                  {/* <VuiBox display="flex" gap={2} mt="auto">
                  <VuiButton
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleReject}
                      fullWidth
                    >
                      Reject
                  </VuiButton>
                  <VuiButton
                    variant="outlined"
                      color="warning" 
                    size="small"
                    onClick={handleHold}
                      fullWidth
                    >
                      Hold
                  </VuiButton>
                  <VuiButton
                    variant="contained"
                    color="info"
                    size="small"
                    onClick={handleAlertMB}
                      fullWidth
                    >
                      Alert MB
                  </VuiButton>
                  </VuiBox> */}
                </VuiBox>
              </VuiBox>
            </Card>
          </Grid>
        </Grid>

        {/* Takedowns Table */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12}>
            <Card sx={{ overflow: "visible" }}>
              <VuiBox p={3}>
                <VuiBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
                  <VuiTypography variant="h6" color="white" fontWeight="bold">
                    Takedowns ({totalEntries})
                  </VuiTypography>
                  <VuiInput
                    placeholder="Search takedowns..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    icon={{
                      component: "search",
                      direction: "left",
                    }}
                    sx={{ width: '300px' }}
                  />
                </VuiBox>
                {tableLoading ? (
                  <VuiBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress color="info" />
                  </VuiBox>
                ) : (
                  <DataTable
                    table={tableData}
                    entriesPerPage={{ 
                      defaultValue: entriesPerPage, 
                      entries: [10, 25, 50, 100] 
                    }}
                    canSearch={false}
                    pagination={{
                      variant: "gradient",
                      color: "info",
                    }}
                    currentPage={currentPage}
                    totalPages={Math.ceil(totalEntries / entriesPerPage)}
                    onPageChange={handlePageChange}
                    onEntriesPerPageChange={handleEntriesPerPageChange}
                  />
                )}
              </VuiBox>
            </Card>
          </Grid>
        </Grid>
      </VuiBox>
    </DashboardLayout>
  );
}

export default ThreatDetails;
